<template>
  <Layout
    class="onboarding-million-users16167"
    :class="{
      'onboarding-million-users16167--loaded': riveRef?.isLoaded,
      'onboarding-million-users16167--show-testimonials':
        finishedIntroAnimation,
    }"
    :title="page.title"
    :subtitle="page.subtitle"
  >
    <div
      v-if="finishedIntroAnimation"
      class="onboarding-million-users16167__testimonials"
      :scrollLeft="testimonialsScrollPosition"
    >
      <MillionUsers16167Testimonial
        v-for="(person, index) in reverseContentArray"
        ref="testimonialsRef"
        :key="person.name"
        :image="getTestimonialImage(person.image)"
        :person="person"
        :isActive="view.currentSubpageIndex === index"
      />
    </div>

    <div class="onboarding-million-users16167__people">
      <img
        v-for="(person, index) in page.content"
        :key="person.name"
        :src="getTestimonialImage(person.image)"
        alt=""
        width="90"
        height="90"
        class="onboarding-million-users16167__person"
        :style="{ '--popup-delay': index }"
      />
    </div>

    <Rive
      ref="riveRef"
      class="onboarding-million-users16167__rive"
      :src="getImage(page.animation.src)"
      fit="Layout"
      @stop="showTestimonials"
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import { MillionUsers16167Page } from '@/models/onboarding.types'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'
import MillionUsers16167Testimonial from '../MillionUsers16167Testimonial.vue'
import { platform } from '@/services/platform'

const props = defineProps<{
  view: QuestionnaireView
}>()

const riveRef = ref<InstanceType<typeof Rive> | null>(null)
const testimonialsRef = ref<
  InstanceType<typeof MillionUsers16167Testimonial>[]
>([])

const finishedIntroAnimation = ref(false)

/**
 * Set the scroll position of the carousel
 * to the item matching the current subpage index.
 */
const testimonialsScrollPosition = computed(() => {
  if (testimonialsRef.value.length === 0) {
    return 0
  }
  const index = props.view.currentSubpageIndex
  const currentTestimonial = testimonialsRef.value[index]
  return currentTestimonial.$el.offsetWidth * (index + 1)
})

const page = computed(() => {
  return props.view.currentPage as MillionUsers16167Page
})

const rive = computed(() => {
  return riveRef.value?.rive
})

const reverseContentArray = computed(() => {
  return [...page.value.content].reverse()
})

function showTestimonials(): void {
  if (finishedIntroAnimation.value) {
    return
  }

  finishedIntroAnimation.value = true

  if (platform.isDesktopWidth()) {
    rive.value?.play('desktopZoom')
  } else {
    rive.value?.play('mobileZoom')
  }

  setTimeout(() => {
    props.view.showContinueButton()
  }, 2300)
}

function getTestimonialImage(image: string): string {
  return require(`@/assets.v2/images/${image}`)
}
</script>

<style lang="scss" scoped>
.onboarding-million-users16167 {
  position: relative;
  overflow: hidden;

  @media (max-height: 840px) {
    --rive-padding-block: calc(150px - 5%);
  }
  @media (max-height: 700px) {
    --rive-padding-block: 200px;
  }
  @media (max-height: 600px) {
    --rive-padding-block: 250px;
  }

  &:deep(.onboarding-layout__default-slot) {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-top: var(--onboarding-padding);
  }

  &:deep(.onboarding-layout__text) {
    position: absolute;
    opacity: 0;
  }

  &:deep(.onboarding-layout__title) {
    font-size: 32px;
    line-height: normal;
    white-space: nowrap;
  }

  &:deep(.onboarding-layout__subtitle) {
    font-size: 20px;
    line-height: 26px;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: $c-black-2;
  }

  &__people {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-areas: 'stack';
    justify-content: center;
    align-items: center;
    max-width: none;
    pointer-events: none;
    @media (max-height: 840px) {
      height: calc(100% + var(--rive-padding-block));
    }
  }

  &__person {
    grid-area: stack;
    width: 90px;
    height: 90px;
    border-radius: 90px;
    border: 6px solid #ff815b;
    transform: translate(var(--person-X), var(--person-Y)) scale(0);

    &:nth-child(1) {
      --person-X: -30px;
      --person-Y: 20px;
    }

    &:nth-child(2) {
      --person-X: 80px;
      --person-Y: -120px;
    }

    &:nth-child(3) {
      --person-X: -80px;
      --person-Y: -190px;
    }
  }

  &__testimonials {
    display: flex;
    gap: var(--onboarding-padding);
    padding-block: 45px;
    padding-inline: var(--onboarding-padding);
    // I don't know why we need these negative margins,
    // but without them the element doesn't streach from
    // one edge of the screen to another, as it should.
    margin-inline: calc(var(--onboarding-padding) * -1);
    width: calc(100% + var(--onboarding-padding) * 2);
    max-width: var(--onboarding-outer-width);
    overflow-x: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // The Rive player is rendered below
  // the rest of the page.
  &__rive {
    position: absolute;
    inset: 0;
    max-width: none;
    z-index: -1;
    @media (max-height: 840px) {
      height: calc(100% + var(--rive-padding-block));
    }
  }

  ~ :deep(.onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
  }
}

.onboarding-million-users16167--loaded {
  :deep(.onboarding-layout__text) {
    animation: fadeIn 1s 2.3s forwards;
  }

  .onboarding-million-users16167 {
    &__person {
      $additionalDelay: calc(var(--popup-delay) * 0.3s);
      $animationDelay: calc(4.8s + $additionalDelay);
      animation: person-popup 0.5s $animationDelay $e-overshoot forwards;
    }
  }
}

.onboarding-million-users16167--show-testimonials {
  :deep(.onboarding-layout__text) {
    animation: fadeOut 0.4s forwards;
  }

  .onboarding-million-users16167 {
    &__people {
      @media (max-height: 840px) {
        height: 100%;
        transition: height 1s;
      }
    }

    &__person:last-child {
      animation:
        person-move-to-top 1s forwards,
        fadeOut 0.1s 1.6s forwards;
    }

    &__person:not(:last-child) {
      opacity: 0;
      transition: opacity 0.2s;
    }

    &__testimonials {
      z-index: 1;
      opacity: 0;
      animation: fadeIn 1s 1s forwards;
    }
  }
}

@keyframes person-popup {
  to {
    transform: translate(var(--person-X), var(--person-Y)) scale(1);
  }
}

@keyframes person-move-to-top {
  from {
    transform: translate(var(--person-X), var(--person-Y));
  }
  to {
    transform: translate(0, -190px);
  }
}
</style>
